(() => {
  const ROOT_SELECTOR = '[data-bewow-combo]';

  const normalize = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const pick = (object, keys) => {
    if (!object || typeof object !== 'object') return undefined;

    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(object, key)) return object[key];
    }

    const normalizedKeys = new Map(Object.keys(object).map((key) => [normalize(key), key]));
    for (const key of keys) {
      const actualKey = normalizedKeys.get(normalize(key));
      if (actualKey) return object[actualKey];
    }

    return undefined;
  };

  const parseMoney = (value) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

    let normalizedValue = String(value || '')
      .replace(/R\$/gi, '')
      .replace(/\s/g, '');

    if (normalizedValue.includes(',')) {
      normalizedValue = normalizedValue.replace(/\./g, '').replace(',', '.');
    }

    const result = Number.parseFloat(normalizedValue);
    return Number.isFinite(result) ? result : 0;
  };

  const parseInteger = (value) => {
    const result = Number.parseInt(value, 10);
    return Number.isFinite(result) ? result : 0;
  };

  const formatCurrency = (value) =>
    Number(value || 0).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });

  const createElement = (tagName, className, text) => {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  // AdGrow (14/09/2026): textos vêm dos campos da seção; marcadores como {n}, {x}, {falta}, {excesso}, {preço}, {total}
  // e {valor} são trocados aqui. Marcador sem valor fica como está, para o erro de digitação aparecer na página.
  const fillTemplate = (template, values) =>
    String(template || '').replace(/\{([^{}]+)\}/g, (match, key) => {
      const chave = key === 'preco' ? 'preço' : key;
      return Object.prototype.hasOwnProperty.call(values, chave) ? String(values[chave]) : match;
    });

  // tira o separador "·" que sobra quando um marcador fica vazio (faixa sem desconto, por exemplo)
  const cleanSeparators = (text) =>
    String(text || '')
      .replace(/(\s*·\s*)+$/, '')
      .replace(/^(\s*·\s*)+/, '')
      .replace(/(\s*·\s*){2,}/g, ' · ')
      .trim();

  // AdGrow · celular (15/09/2026): a linha de preço do seletor vira partes separadas por " · ". No desktop elas seguem na
  // mesma linha, com o mesmo texto; no celular o CSS esconde o separador e põe uma parte embaixo da outra.
  const fillDetail = (element, text) => {
    element.replaceChildren();
    String(text || '')
      .split(' · ')
      .forEach((part, index) => {
        if (index) element.append(createElement('span', 'bewow-combo-builder__detail-sep', ' · '));
        element.append(createElement('span', 'bewow-combo-builder__detail-part', part));
      });
  };

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // AdGrow (14/09/2026, P18): aria-disabled no lugar de disabled, para o foco não cair no body quando o botão trava
  const setAriaDisabled = (element, value) => {
    if (value) element.setAttribute('aria-disabled', 'true');
    else element.removeAttribute('aria-disabled');
  };

  class BewowComboBuilder {
    constructor(root) {
      this.root = root;
      this.configElement = root.querySelector('[data-combo-config]');
      // AdGrow (14/09/2026, ajustes do Rodrigo, 2ª rodada): seletor de caixa com face desenhada e painel de compra no fim dos
      // sabores (progresso por garrafa, valor da caixa e botão)
      this.tierButton = root.querySelector('[data-combo-tier-button]');
      this.tierList = root.querySelector('[data-combo-tier-list]');
      // AdGrow (23/09/2026): modo grid da etapa 1. As caixas ficam sempre à vista, então abrir e fechar não se aplica.
      this.tierGrade = !!root.querySelector('[data-combo-tier-grade]');
      this.tierSelectWrap = this.tierButton ? this.tierButton.closest('.bewow-combo-builder__select') : null;
      this.tierOptions = [];
      this.activeOption = 0;
      this.tierTitle = root.querySelector('[data-combo-tier-title]');
      this.tierDetail = root.querySelector('[data-combo-tier-detail]');
      this.tierOff = root.querySelector('[data-combo-tier-off]');
      this.flavorsElement = root.querySelector('[data-combo-flavors]');
      this.flavorsHeading = root.querySelector('[data-combo-flavors-heading]');
      this.categoriesElement = root.querySelector('[data-combo-categories]');
      this.progressElement = root.querySelector('[data-combo-progress]');
      this.progressCount = root.querySelector('[data-progress-count]');
      this.progressLeft = root.querySelector('[data-progress-left]');
      this.progressSlots = root.querySelector('[data-progress-slots]');
      this.progressCapacity = 0;
      this.compareLine = root.querySelector('[data-combo-compare-line]');
      this.savingsElement = root.querySelector('[data-combo-savings]');
      this.totalElement = root.querySelector('[data-combo-total]');
      this.compareElement = root.querySelector('[data-combo-compare]');
      this.unitPriceElement = root.querySelector('[data-combo-unit-price]');
      this.buyButton = root.querySelector('[data-combo-buy]');
      // AdGrow (23/09/2026): três ações no painel. Os três adicionam a caixa; muda o destino.
      this.buyAlts = root.querySelector('[data-combo-buy-alts]');
      this.buyAnother = root.querySelector('[data-combo-buy-another]');
      this.buyCheckout = root.querySelector('[data-combo-buy-checkout]');
      this.messageElement = root.querySelector('[data-combo-message]');
      this.counts = Object.create(null);
      this.manualTier = false;
      this.tierIndex = 0;
      this.categoryCounters = [];
      this.noticeActive = false;
      this.adding = false;
      // AdGrow (14/09/2026, pedido do Rodrigo): sugestões de sabores
      this.presetsRoot = root.querySelector('[data-combo-presets]');
      this.presetButtons = Array.from(root.querySelectorAll('[data-combo-preset]'));
      this.presetHint = root.querySelector('[data-combo-preset-hint]');
      this.presetUndo = root.querySelector('[data-combo-preset-undo]');
      this.presetClear = root.querySelector('[data-combo-preset-clear]');
      this.presetApplied = null;
      this.presetUndoState = null;
      // AdGrow (24/09/2026): só as primeiras sugestões ficam à vista; o botão revela o resto no lugar
      this.presetToggle = root.querySelector('[data-combo-preset-toggle]');
      this.presetExpanded = false;
      // AdGrow (24/09/2026, frente A2): barra compacta. "Ver caixa" abre os detalhes; depois de adicionar, a barra vira a
      // confirmação (Ir para o carrinho / Montar outra caixa). A etapa 1 é observada para tirar o WhatsApp de cima dos cartões.
      this.bar = root.querySelector('[data-combo-bar]');
      this.detailToggle = root.querySelector('[data-combo-detail-toggle]');
      this.detail = root.querySelector('[data-combo-detail]');
      this.detailList = root.querySelector('[data-combo-detail-list]');
      this.detailEmpty = root.querySelector('[data-combo-detail-empty]');
      this.doneBox = root.querySelector('[data-combo-done]');
      this.goCart = root.querySelector('[data-combo-go-cart]');
      this.tierElement = root.querySelector('.bewow-combo-builder__tier');
      this.detailOpen = false;
      this.confirmed = false;
      this.confirmedSnapshot = '';
      // AdGrow (24/09/2026, card de resumo em três estados): selo com a quantidade, contagem de sabores, título da lista,
      // bloco de ações da caixa completa e faixa de confirmação
      this.actionsBox = root.querySelector('[data-combo-actions]');
      this.doneBanner = root.querySelector('[data-combo-done-banner]');
      this.badgeNumber = root.querySelector('[data-combo-badge-n]');
      this.flavorCount = root.querySelector('[data-combo-flavor-count]');
      this.listTitle = root.querySelector('[data-combo-list-title]');
      // AdGrow (24/09/2026, frente C2): modo edição. O carrinho manda para ?editar=<_Montagem>; o construtor abre com a
      // caixa carregada e o principal vira "Salvar alterações", que troca as linhas antigas pelas novas com o mesmo id.
      this.editNotice = root.querySelector('[data-combo-edit-notice]');
      this.editNoticeText = root.querySelector('[data-combo-edit-text]');
      this.editNoticeLink = root.querySelector('[data-combo-edit-cancel]');
      this.editando = null;

      if (!this.configElement || !this.tierButton || !this.tierList || !this.categoriesElement || !this.buyButton) return;

      try {
        this.config = JSON.parse(this.configElement.textContent);
      } catch (error) {
        this.showMessage('Não foi possível ler a configuração do combo. Revise o JSON do produto.');
        return;
      }

      this.texts = this.config.texts && typeof this.config.texts === 'object' ? this.config.texts : {};
      // AdGrow (24/09/2026): foto real por sabor (produto avulso), chave pelo nome normalizado da variante
      this.flavorImages = new Map(
        Object.entries(this.config.flavorImages && typeof this.config.flavorImages === 'object' ? this.config.flavorImages : {})
          .map(([nome, foto]) => [normalize(nome), foto])
          .filter(([nome, foto]) => nome && foto && foto.src)
      );
      this.presetVisibleCount = Math.max(0, parseInteger(this.config.presetVisibleCount));
      // AdGrow (24/09/2026, etapa 01 no layout novo): selo de destaque por tamanho de caixa, do campo "Selos das caixas",
      // uma linha por caixa no formato "12 = Mais vendida"
      this.tierSelos = new Map(
        String(this.config.tierSelos || '')
          .split(/\r?\n/)
          .map((linha) => linha.split('='))
          .filter((partes) => partes.length === 2 && parseInteger(partes[0]) > 0 && partes[1].trim())
          .map((partes) => [parseInteger(partes[0]), partes[1].trim()])
      );
      this.data = this.resolveData(this.config.bundleData, this.config.fallbackJson);
      this.variants = Array.isArray(this.config.variants) ? this.config.variants : [];
      // AdGrow (16/09/2026, pedido do Rodrigo): sabor listado em "Sabores ocultos" sai da vitrine sem perder cadastro
      // nem estoque. O corte e na origem, entao ele some dos mapas, da rede de seguranca "Outros sabores" e das sugestoes.
      this.hiddenFlavors = new Set(
        (Array.isArray(this.config.hiddenFlavors) ? this.config.hiddenFlavors : [])
          .map((linha) => normalize(linha))
          .filter(Boolean)
      );
      if (this.hiddenFlavors.size) {
        this.variants = this.variants.filter((variant) => !this.hiddenFlavors.has(normalize(variant.name)));
      }
      this.variantByName = new Map(this.variants.map((variant) => [normalize(variant.name), variant]));
      this.variantById = new Map(this.variants.map((variant) => [String(variant.id), variant]));
      this.tiers = this.normalizeTiers(this.data);
      // AdGrow (14/09/2026): categorias vêm dos blocos da seção quando existirem; o metafield de preços segue só com as faixas
      // cada linha de sabor aceita "Nome exibido = nome da variante"; sem " = ", o nome exibido é o próprio nome da variante
      const categoriasDosBlocos = (Array.isArray(this.config.categoriesData) ? this.config.categoriesData : []).map((categoria) => ({
        ...categoria,
        sabores: (categoria.sabores || []).map((linha) => {
          const texto = String(linha || '').trim();
          const partes = texto.split(/\s+=\s+/);
          return partes.length === 2 ? { name: partes[0].trim(), variantName: partes[1].trim() } : texto;
        }).filter((sabor) => {
          if (!this.hiddenFlavors.size) return true;
          const exibido = typeof sabor === 'object' ? sabor.name : sabor;
          const variante = typeof sabor === 'object' ? sabor.variantName : sabor;
          return !this.hiddenFlavors.has(normalize(exibido)) && !this.hiddenFlavors.has(normalize(variante));
        }),
      })).filter((categoria) => categoria.sabores.length);
      this.categories = this.normalizeCategories(categoriasDosBlocos.length ? { categorias: categoriasDosBlocos } : this.data);
      if (categoriasDosBlocos.length && this.categories.length) {
        // sabor cadastrado no produto e esquecido nas categorias não pode sumir da vitrine
        const listados = new Set(this.categories.flatMap((categoria) => categoria.flavors.map((sabor) => sabor.key)));
        const esquecidos = this.variants.map((variant) => this.normalizeFlavor(variant)).filter((sabor) => sabor && !listados.has(sabor.key));
        if (esquecidos.length) this.categories.push({ name: 'Outros sabores', open: true, flavors: esquecidos });
      }

      if (!this.tiers.length) {
        const fallbackTier = this.config.fallbackTier || {};
        this.tiers = [
          {
            quantity: Math.max(1, parseInteger(fallbackTier.quantity)),
            unitPrice: Math.max(0, parseMoney(fallbackTier.unitPrice)),
            discount: '',
            label: '',
          },
        ];
      }

      if (!this.categories.length) {
        this.categories = [
          {
            name: this.config.fallbackCategory || 'Sabores',
            open: true,
            flavors: this.variants.map((variant) => this.normalizeFlavor(variant)).filter(Boolean),
          },
        ];
      }

      this.tiers.sort((first, second) => first.quantity - second.quantity);
      this.maxQuantity = Math.max(...this.tiers.map((tier) => tier.quantity));
      // AdGrow (14/09/2026, ajustes do Rodrigo): a página abre com a caixa do campo "Caixa selecionada ao abrir" (12 garrafas),
      // então o valor aparece antes de o cliente escolher os sabores. Sem caixa desse tamanho, abre na menor.
      const caixaInicial = this.tiers.findIndex((tier) => tier.quantity === parseInteger(this.config.defaultQuantity));
      this.tierIndex = caixaInicial >= 0 ? caixaInicial : 0;
      this.renderTiers();
      this.fitTierFlags();
      if (this.tierGrade && 'ResizeObserver' in window) {
        let quadro = 0;
        new ResizeObserver(() => {
          cancelAnimationFrame(quadro);
          quadro = requestAnimationFrame(() => this.fitTierFlags());
        }).observe(this.tierList);
      }
      // a fonte da página pode chegar depois da primeira medida
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.fitTierFlags());
      this.renderCategories();
      this.bindEvents();
      this.observeViewport();
      this.updateUI();
      this.checkEdit();
    }

    // AdGrow (24/09/2026, frente C2): fetch com prazo de 15 s, o mesmo cuidado do addToCart (17/09/2026). Sem prazo, uma
    // requisição pendurada deixava o botão travado. Devolve o JSON ou lança erro com a descrição da Shopify.
    async cartRequest(path, body) {
      const controle = new AbortController();
      const prazo = setTimeout(() => controle.abort(), 15000);
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}${path}`, {
          method: body ? 'POST' : 'GET',
          headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
          body: body ? JSON.stringify(body) : undefined,
          signal: controle.signal,
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.description || data.message || 'cart');
        return data;
      } finally {
        clearTimeout(prazo);
      }
    }

    // linhas do carrinho que pertencem a uma caixa (mesmo _Montagem, gravado pelo construtor desde 24/09/2026)
    linesOfBox(cart, id) {
      return (cart && Array.isArray(cart.items) ? cart.items : []).filter(
        (item) => item.properties && item.properties._Tipo === 'Item de Pacote' && item.properties._Montagem === id
      );
    }

    // AdGrow (24/09/2026, frente C2): ?editar=<_Montagem> na URL. Busca a caixa no carrinho e carrega as quantidades por
    // variante; a caixa sai pela soma (6/12/18/24). Caixa já removida ou id inválido: abre vazio, sem erro, e limpa a URL.
    // Caixa com sabor que o construtor não oferece mais (oculto ou esgotado): não entra em edição, porque salvar apagaria
    // esse sabor da caixa; avisa e aponta o carrinho.
    async checkEdit() {
      let id = '';
      try {
        id = new URLSearchParams(window.location.search).get('editar') || '';
      } catch (e) { id = ''; }
      if (!id) return;
      const limparUrl = () => {
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('editar');
          window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
        } catch (e) { /* URL fica como está */ }
      };
      let linhas = [];
      try {
        linhas = this.linesOfBox(await this.cartRequest('cart.js'), id);
      } catch (e) {
        limparUrl();
        return;
      }
      if (!linhas.length) {
        limparUrl();
        return;
      }

      const chavePorVariante = new Map();
      this.root.querySelectorAll('[data-flavor-key]').forEach((row) => {
        if (row.dataset.variantId && row.dataset.available === 'true') chavePorVariante.set(String(row.dataset.variantId), row.dataset.flavorKey);
      });
      const soma = linhas.reduce((total, linha) => total + (linha.quantity || 0), 0);
      const todasNoConstrutor = linhas.every((linha) => chavePorVariante.has(String(linha.variant_id)));
      if (!todasNoConstrutor || soma > this.maxQuantity) {
        limparUrl();
        this.showEditNotice(this.texts.editUnavailable || 'Um dos sabores desta caixa não está disponível para montar agora. Ajuste pelo carrinho.', this.config.goCartText || 'Ir para o carrinho');
        return;
      }

      Object.keys(this.counts).forEach((key) => { this.counts[key] = 0; });
      linhas.forEach((linha) => {
        const key = chavePorVariante.get(String(linha.variant_id));
        this.counts[key] = (this.counts[key] || 0) + linha.quantity;
      });
      const exata = this.tiers.findIndex((tier) => tier.quantity === soma);
      const cabe = this.tiers.findIndex((tier) => tier.quantity >= soma);
      this.tierIndex = exata >= 0 ? exata : cabe >= 0 ? cabe : this.tiers.length - 1;
      // a caixa carregada conta como escolhida à mão: não sobe sozinha e as sugestões a tratam como seleção do cliente
      this.manualTier = true;
      this.presetApplied = null;
      this.presetUndoState = null;
      this.editando = { id };
      this.setConfirmed(false);
      this.root.classList.add('is-editando');
      this.updateUI();
      this.showEditNotice(this.texts.editNotice || 'Você está editando a caixa do seu carrinho. Ao salvar, ela é atualizada.', this.config.editCancelText || 'Cancelar');
      if (this.editNotice && !this.editNotice.hidden) {
        this.editNotice.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
      }
    }

    showEditNotice(texto, rotuloLink) {
      if (!this.editNotice || !this.editNoticeText) return;
      this.editNoticeText.textContent = texto;
      if (this.editNoticeLink) {
        this.editNoticeLink.textContent = rotuloLink;
        this.editNoticeLink.hidden = !rotuloLink;
      }
      this.editNotice.hidden = false;
    }

    resolveData(bundleData, fallbackJson) {
      let result = bundleData;

      if (typeof result === 'string') {
        try {
          result = JSON.parse(result);
        } catch (error) {
          result = null;
        }
      }

      if ((result === null || result === undefined || result === '') && fallbackJson) {
        try {
          result = typeof fallbackJson === 'string' ? JSON.parse(fallbackJson) : fallbackJson;
        } catch (error) {
          this.showMessage('O JSON alternativo é inválido. Revise vírgulas, aspas e chaves.');
        }
      }

      return result || {};
    }

    looksLikeTier(item) {
      return Boolean(
        item &&
          typeof item === 'object' &&
          pick(item, ['quantity', 'quantidade', 'Quantidade', 'qty']) !== undefined &&
          pick(item, ['unitPrice', 'unit_price', 'valor_unitario', 'Valor', 'valor', 'price']) !== undefined
      );
    }

    normalizeTiers(data) {
      let source = [];

      if (Array.isArray(data) && data.some((item) => this.looksLikeTier(item))) {
        source = data;
      } else {
        source = pick(data, ['tiers', 'faixas', 'precos', 'pricing', 'prices', 'kits']) || [];
      }

      if (!Array.isArray(source)) source = Object.values(source || {});

      return source
        .map((tier) => ({
          quantity: parseInteger(pick(tier, ['quantity', 'quantidade', 'Quantidade', 'qty'])),
          unitPrice: parseMoney(
            pick(tier, ['unitPrice', 'unit_price', 'valor_unitario', 'Valor', 'valor', 'price'])
          ),
          discount: String(pick(tier, ['discount', 'desconto', 'Desconto', 'badge']) || ''),
          label: String(pick(tier, ['label', 'rotulo', 'nome', 'name']) || ''),
        }))
        .filter((tier) => tier.quantity > 0 && tier.unitPrice >= 0);
    }

    normalizeCategories(data) {
      let source = pick(data, ['categories', 'categorias', 'groups', 'grupos']);

      if (!source) {
        const flatFlavors = pick(data, ['flavors', 'flavours', 'sabores', 'items', 'itens']);
        if (Array.isArray(flatFlavors)) return this.groupFlatFlavors(flatFlavors);
      }

      if (!source && Array.isArray(data) && !data.some((item) => this.looksLikeTier(item))) {
        return this.groupFlatFlavors(data);
      }

      if (!source) return [];

      if (!Array.isArray(source) && typeof source === 'object') {
        source = Object.entries(source).map(([name, flavors]) => ({ name, flavors }));
      }

      if (!Array.isArray(source)) return [];

      return source
        .map((category, index) => {
          if (typeof category === 'string') return null;

          const name = String(pick(category, ['name', 'nome', 'title', 'titulo', 'categoria']) || `Categoria ${index + 1}`);
          let flavors = pick(category, ['flavors', 'flavours', 'sabores', 'items', 'itens', 'products', 'produtos']) || [];
          if (!Array.isArray(flavors)) flavors = Object.values(flavors || {});

          return {
            name,
            open: pick(category, ['open', 'aberta', 'expanded']) !== false,
            flavors: this.dedupeFlavors(flavors.map((flavor) => this.normalizeFlavor(flavor)).filter(Boolean)),
          };
        })
        .filter((category) => category && category.flavors.length);
    }

    groupFlatFlavors(flavors) {
      const groups = new Map();

      flavors.forEach((item) => {
        const categoryName =
          (item && typeof item === 'object' &&
            pick(item, ['category', 'categoria', 'group', 'grupo', 'categoryName', 'nome_categoria'])) ||
          this.config.fallbackCategory ||
          'Sabores';
        const key = String(categoryName);
        if (!groups.has(key)) groups.set(key, []);
        const flavor = this.normalizeFlavor(item);
        if (flavor) groups.get(key).push(flavor);
      });

      return Array.from(groups, ([name, items]) => ({
        name,
        open: true,
        flavors: this.dedupeFlavors(items),
      })).filter((category) => category.flavors.length);
    }

    normalizeFlavor(item) {
      const isObject = item && typeof item === 'object';
      const name = String(
        isObject ? pick(item, ['name', 'nome', 'flavor', 'flavour', 'sabor', 'title', 'titulo']) || '' : item || ''
      ).trim();
      if (!name) return null;

      const requestedVariantId = isObject
        ? pick(item, ['variantId', 'variant_id', 'id_variante', 'variant', 'id'])
        : null;
      // AdGrow (14/09/2026): o nome exibido pode ser diferente do nome da variante ("Nome exibido = nome da variante" no bloco de categoria)
      const variantName = isObject ? String(pick(item, ['variantName', 'nome_variante']) || name) : name;
      const matchedVariant = requestedVariantId
        ? this.variantById?.get(String(requestedVariantId))
        : this.variantByName?.get(normalize(variantName));
      const variantId = matchedVariant?.id || requestedVariantId || null;
      const available = matchedVariant ? matchedVariant.available !== false : Boolean(variantId);
      const key = variantId ? `variant-${variantId}` : `flavor-${normalize(name)}`;

      return { name, variantName, key, variantId, available };
    }

    dedupeFlavors(flavors) {
      const seen = new Set();
      return flavors.filter((flavor) => {
        if (seen.has(flavor.key)) return false;
        seen.add(flavor.key);
        return true;
      });
    }

    // AdGrow (14/09/2026, ajustes do Rodrigo, 3ª rodada): lista de caixas desenhada pelo tema, uma opção por faixa de preço
    // (caixa nova aparece sozinha). Cada opção repete a face: título, linha de preço e etiqueta de desconto.
    renderTiers() {
      this.tierList.replaceChildren();
      const sectionId = this.root.dataset.sectionId || 'combo';

      this.tierOptions = this.tiers.map((tier, index) => {
        const values = this.tierValues(tier);
        const option = createElement('li', 'bewow-combo-builder__option');
        option.id = `BewowComboTierOption-${sectionId}-${index}`;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', String(index === this.tierIndex));
        option.dataset.index = String(index);

        // AdGrow (24/09/2026, etapa 01 no layout novo, mockup do Rodrigo): no grid cada caixa vira um cartão com a bolinha de
        // seleção, "N garrafas" e o selo de % OFF no topo; o total com "total da caixa"; o preço por garrafa; e, no rodapé, a
        // economia e o selo de destaque do campo "Selos das caixas". Textos todos de campo; a conta é a de tierValues().
        if (this.tierGrade) {
          option.classList.add('bewow-combo-builder__option--cartao');
          const topo = createElement('span', 'bewow-caixa__topo');
          const radio = createElement('span', 'bewow-caixa__radio');
          radio.setAttribute('aria-hidden', 'true');
          topo.append(radio, createElement('span', 'bewow-caixa__titulo', cleanSeparators(fillTemplate(this.texts.tierFaceTitle || '{n} garrafas', values))));
          // AdGrow (24/09/2026, pedido do Rodrigo): o selo de destaque ("Mais vendida") vira etiqueta ao lado do % OFF, as duas
          // apoiadas na borda de cima do cartão
          const selo = this.tierSelos.get(tier.quantity);
          const etiquetas = createElement('span', 'bewow-caixa__etiquetas');
          if (selo) etiquetas.append(createElement('span', 'bewow-caixa__selo bewow-caixa__selo--etiqueta', selo));
          if (tier.discount && this.config.tierShowOff) etiquetas.append(createElement('span', 'bewow-caixa__off', tier.discount));
          if (etiquetas.childNodes.length) topo.append(etiquetas);
          const preco = createElement('span', 'bewow-caixa__preco');
          preco.append(createElement('b', 'bewow-caixa__total', values.total));
          if (this.texts.tierTotalLabel) preco.append(createElement('span', 'bewow-caixa__total-rotulo', this.texts.tierTotalLabel));
          option.append(topo, preco);
          if (this.texts.tierUnitLabel !== '') {
            // no cartão estreito (celular até 390 px) a linha não cabe inteira; as duas últimas palavras ficam juntas, para a
            // quebra cair em "R$ 16,00 / por garrafa" e não em "R$ 16,00 por / garrafa"
            const unitTexto = fillTemplate(this.texts.tierUnitLabel || '{preço} por garrafa', values).replace(/ (\S+)$/, '\u00a0$1');
            option.append(createElement('span', 'bewow-caixa__unit', unitTexto));
          }
          const rodape = createElement('span', 'bewow-caixa__rodape');
          const economiaCartao = this.texts.tierSavings ? fillTemplate(this.texts.tierSavings, values) : '';
          if (economiaCartao && values.economiaBruta > 0) rodape.append(createElement('span', 'bewow-caixa__economia', economiaCartao));
          // o rodapé existe sempre: é para onde o % OFF desce quando as etiquetas não cabem na borda (fitTierFlags)
          option.append(rodape);
          this.tierList.append(option);
          return option;
        }

        const mark = createElement('span', 'bewow-combo-builder__option-mark');
        mark.setAttribute('aria-hidden', 'true');
        const text = createElement('span', 'bewow-combo-builder__option-text');
        const title = createElement('span', 'bewow-combo-builder__option-title');
        // AdGrow (24/09/2026): ícone de confirmação da caixa escolhida (o CSS só o mostra no cartão
        // selecionado, em modo grid). Fica dentro do título para poder ir em linha com o texto.
        const check = createElement('span', 'bewow-combo-builder__option-check');
        check.setAttribute('aria-hidden', 'true');
        title.append(check, cleanSeparators(fillTemplate(this.texts.tierFaceTitle || 'Caixa com {n} garrafas', values)));
        text.append(title);
        const detail = this.texts.tierFaceDetail ? cleanSeparators(fillTemplate(this.texts.tierFaceDetail, values)) : '';
        if (detail) {
          const detailElement = createElement('span', 'bewow-combo-builder__option-detail');
          fillDetail(detailElement, detail);
          text.append(detailElement);
        }
        // AdGrow (23/09/2026, pedido do Rodrigo): quanto a caixa economiza contra a garrafa avulsa.
        // Some quando não há diferença, para nunca mostrar "Economize R$ 0,00".
        const economiaTexto = this.texts.tierSavings ? fillTemplate(this.texts.tierSavings, values) : '';
        if (economiaTexto && values.economiaBruta > 0) {
          text.append(createElement('span', 'bewow-combo-builder__option-savings', economiaTexto));
        }

        // AdGrow (23/09/2026, 2ª versão a pedido do Rodrigo): o rótulo existe nos quatro cartões.
        // No escolhido diz "Escolhida"; nos outros vira o convite "Escolher", que preenche o espaço
        // (sem rodapé vazio) e avisa que o cartão é tocável. Com o convite em branco no editor,
        // volta ao espaço reservado invisível.
        if (this.texts.tierChosen) {
          const escolhido = index === this.tierIndex;
          const rotulo = createElement(
            'span',
            'bewow-combo-builder__option-chosen',
            escolhido ? this.texts.tierChosen : this.texts.tierChoose || this.texts.tierChosen
          );
          rotulo.classList.toggle('is-oculto', !escolhido && !this.texts.tierChoose);
          text.append(rotulo);
        }

        option.append(mark, text);
        // AdGrow (24/09/2026): no grid o selo de % OFF só entra com o campo "Selo de % OFF nos cartões" ligado;
        // a economia em reais ("Economize R$ X") é a única indicação por padrão. No seletor fechado nada muda.
        if (tier.discount && (!this.tierGrade || this.config.tierShowOff)) {
          option.append(createElement('span', 'bewow-combo-builder__option-off', tier.discount));
        }

        this.tierList.append(option);
        return option;
      });
    }

    // AdGrow (24/09/2026): as etiquetas da borda (destaque e % OFF) precisam caber na largura do cartão. No celular pequeno
    // (cartão de 143 a 178 px) as duas não cabem juntas; aí o % OFF desce para o rodapé, ao lado do "Economize", em vez de
    // sair do cartão. O destaque ("Mais vendida", "Mais economia") fica sempre na borda. Refeito quando a largura muda.
    fitTierFlags() {
      if (!this.tierGrade) return;
      const cartoes = this.tierOptions
        .map((option) => ({
          option,
          etiquetas: option.querySelector('.bewow-caixa__etiquetas'),
          off: option.querySelector('.bewow-caixa__off'),
          rodape: option.querySelector('.bewow-caixa__rodape'),
        }))
        .filter((cartao) => cartao.etiquetas && cartao.off && cartao.rodape);
      // primeiro tudo de volta na borda, para medir
      cartoes.forEach(({ etiquetas, off }) => {
        if (off.parentElement !== etiquetas) etiquetas.append(off);
        off.classList.remove('bewow-caixa__off--rodape');
      });
      const duplas = cartoes.filter(({ etiquetas }) => etiquetas.children.length > 1);
      // uniforme: se um cartão com destaque não comporta as duas etiquetas, todos os cartões com destaque descem o % OFF
      const apertado = duplas.some(({ option, etiquetas }) => {
        const esquerda = Math.min(...Array.from(etiquetas.children).map((item) => item.getBoundingClientRect().left));
        return esquerda < option.getBoundingClientRect().left + 2;
      });
      if (!apertado) return;
      duplas.forEach(({ off, rodape }) => {
        off.classList.add('bewow-caixa__off--rodape');
        rodape.append(off);
      });
    }

    isTierListOpen() {
      return !this.tierList.hidden;
    }

    openTierList() {
      if (this.tierGrade) return;
      if (this.isTierListOpen()) return;
      this.tierList.hidden = false;
      this.tierButton.setAttribute('aria-expanded', 'true');
      if (this.tierSelectWrap) {
        this.tierSelectWrap.classList.add('is-aberto');
        // abre para cima quando não cabe embaixo e há mais espaço em cima
        this.tierSelectWrap.classList.remove('is-acima');
        const face = this.tierButton.getBoundingClientRect();
        const below = window.innerHeight - face.bottom;
        this.tierSelectWrap.classList.toggle('is-acima', below < this.tierList.offsetHeight + 16 && face.top > below);
      }
      this.setActiveOption(this.tierIndex);
      this.tierList.focus({ preventScroll: true });
    }

    closeTierList(returnFocus) {
      if (this.tierGrade) return;
      if (!this.isTierListOpen()) return;
      this.tierList.hidden = true;
      this.tierList.removeAttribute('aria-activedescendant');
      this.tierButton.setAttribute('aria-expanded', 'false');
      if (this.tierSelectWrap) this.tierSelectWrap.classList.remove('is-aberto', 'is-acima');
      if (returnFocus) this.tierButton.focus({ preventScroll: true });
    }

    setActiveOption(index) {
      const option = this.tierOptions[index];
      if (!option) return;
      this.activeOption = index;
      this.tierOptions.forEach((item, itemIndex) => item.classList.toggle('is-ativa', itemIndex === index));
      this.tierList.setAttribute('aria-activedescendant', option.id);
      // rola só a lista, nunca a página
      const top = option.offsetTop;
      const bottom = top + option.offsetHeight;
      if (top < this.tierList.scrollTop) this.tierList.scrollTop = top;
      else if (bottom > this.tierList.scrollTop + this.tierList.clientHeight) this.tierList.scrollTop = bottom - this.tierList.clientHeight;
    }

    chooseTier(index) {
      const chosen = Math.max(0, Math.min(this.tiers.length - 1, index));
      this.closeTierList(true);
      this.clearNotice();
      this.manualTier = true;
      // AdGrow (24/09/2026): a escolha com o mouse também passa a ser o ponto de partida das setas do teclado. Antes, o foco
      // na lista (que vem no mousedown, antes do clique) deixava a seta partindo da caixa anterior.
      if (this.tierGrade) this.setActiveOption(chosen);
      if (chosen === this.tierIndex) return;
      this.tierIndex = chosen;
      this.updateUI();
      this.flashPrice();
    }

    // o valor do painel de compra pulsa quando a caixa muda, para o cliente ver que o preço acompanhou a troca
    flashPrice() {
      if (prefersReducedMotion() || !this.totalElement) return;
      this.totalElement.classList.remove('is-atualizado');
      void this.totalElement.offsetWidth;
      this.totalElement.classList.add('is-atualizado');
    }

    tierValues(tier) {
      // AdGrow (23/09/2026): a economia sai da mesma conta do painel, a diferença para a garrafa
      // avulsa (getReferenceUnitPrice). Fica aqui para o cartão da etapa 1 usar o mesmo número.
      const caixa = tier.quantity * tier.unitPrice;
      const referencia = tier.quantity * this.getReferenceUnitPrice();
      const economia = Math.max(0, referencia - caixa);
      return {
        n: tier.quantity,
        'preço': formatCurrency(tier.unitPrice),
        total: formatCurrency(caixa),
        desconto: tier.discount || '',
        valor: formatCurrency(economia),
        economiaBruta: economia,
      };
    }

    renderCategories() {
      this.categoriesElement.replaceChildren();
      this.categoryCounters = [];
      const sectionId = this.root.dataset.sectionId || 'combo';
      let flavorIndex = 0;

      this.categories.forEach((category) => {
        const details = createElement('details', 'bewow-combo-category');
        details.open = category.open;
        const summary = createElement('summary');
        // AdGrow (14/09/2026, P19): quantos sabores já escolhidos, no título, para a categoria recolhida não esconder a seleção
        const summaryTitle = createElement('span', 'bewow-combo-category__title', category.name);
        const summaryCount = createElement('span', 'bewow-combo-category__count');
        summaryTitle.append(summaryCount);
        summary.append(summaryTitle);
        this.categoryCounters.push({ element: summaryCount, keys: category.flavors.map((flavor) => flavor.key) });
        const items = createElement('div', 'bewow-combo-category__items');

        category.flavors.forEach((flavor) => {
          if (!Object.prototype.hasOwnProperty.call(this.counts, flavor.key)) this.counts[flavor.key] = 0;

          const row = createElement('div', 'bewow-combo-flavor');
          row.dataset.flavorKey = flavor.key;
          row.dataset.variantId = flavor.variantId || '';
          row.dataset.available = String(flavor.available);

          const name = createElement('span', 'bewow-combo-flavor__name', flavor.name);
          // AdGrow (24/09/2026): miniatura com a foto real do sabor (produto avulso). Sem foto mapeada,
          // um marcador neutro do mesmo tamanho, para as linhas ficarem alinhadas.
          const foto = this.flavorImage(flavor);
          let thumb;
          if (foto) {
            thumb = createElement('img', 'bewow-combo-flavor__thumb');
            thumb.src = foto.src;
            if (foto.src2x) thumb.srcset = `${foto.src} 1x, ${foto.src2x} 2x`;
            thumb.alt = flavor.name;
            thumb.loading = 'lazy';
            thumb.decoding = 'async';
            thumb.width = 96;
            thumb.height = 96;
          } else {
            thumb = createElement('span', 'bewow-combo-flavor__thumb bewow-combo-flavor__thumb--vazio');
            thumb.setAttribute('aria-hidden', 'true');
          }
          const controls = createElement('div', 'bewow-combo-flavor__controls');
          const decrease = createElement('button', 'bewow-combo-flavor__button', '−');
          const input = createElement('input', 'bewow-combo-flavor__input');
          const increase = createElement('button', 'bewow-combo-flavor__button', '+');

          decrease.type = 'button';
          decrease.dataset.comboAction = 'decrease';
          decrease.setAttribute('aria-label', `Remover uma unidade de ${flavor.name}`);
          input.type = 'number';
          input.value = '0';
          input.min = '0';
          input.max = String(this.maxQuantity);
          input.inputMode = 'numeric';
          input.dataset.comboInput = '';
          input.setAttribute('aria-label', `Quantidade para ${flavor.name}`);
          increase.type = 'button';
          increase.dataset.comboAction = 'increase';
          increase.setAttribute('aria-label', `Adicionar uma unidade de ${flavor.name}`);

          if (!flavor.available) {
            row.classList.add('is-unavailable');
            input.disabled = true;
            setAriaDisabled(increase, true);
            row.title = 'Sabor indisponível neste produto';
            // AdGrow (14/09/2026, P07): etiqueta visível ao lado do nome, lida pelos controles da linha
            const badge = createElement('span', 'bewow-combo-flavor__badge', this.texts.soldOut || 'Esgotado');
            badge.id = `BewowComboSoldOut-${sectionId}-${flavorIndex}`;
            name.append(' ', badge);
            [decrease, input, increase].forEach((element) => element.setAttribute('aria-describedby', badge.id));
          }
          flavorIndex += 1;

          controls.append(decrease, input, increase);
          row.append(thumb, name, controls);
          items.append(row);
        });

        details.append(summary, items);
        this.categoriesElement.append(details);
      });

      if (!this.categoriesElement.children.length) {
        this.showMessage('Nenhum sabor do JSON corresponde às variantes deste produto.');
      }
    }

    // AdGrow (24/09/2026): foto do sabor pelo nome da variante; cai para o nome exibido quando a categoria usa alias
    flavorImage(flavor) {
      if (!this.flavorImages || !this.flavorImages.size) return null;
      return this.flavorImages.get(normalize(flavor.variantName)) || this.flavorImages.get(normalize(flavor.name)) || null;
    }

    bindEvents() {
      // AdGrow (14/09/2026, P05): aviso não fica preso na tela. Some no próximo clique; a fase de captura roda antes
      // da ação do clique, então um aviso novo gerado por esse mesmo clique continua aparecendo.
      document.addEventListener('click', () => this.clearNotice(), true);

      // AdGrow (14/09/2026, ajustes do Rodrigo, 3ª rodada): seletor de caixa próprio
      this.tierButton.addEventListener('click', () => {
        if (this.isTierListOpen()) this.closeTierList(true);
        else this.openTierList();
      });

      this.tierButton.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        event.preventDefault();
        this.openTierList();
      });

      // AdGrow (23/09/2026, revisão de frontend): no grid a lista recebe o foco direto pelo Tab.
      // Sem isto a primeira seta partia do índice 0, e não de onde o cliente está.
      this.tierList.addEventListener('focus', () => {
        if (this.tierGrade) this.setActiveOption(this.tierIndex);
      });

      this.tierList.addEventListener('click', (event) => {
        const option = event.target.closest('[role="option"]');
        if (option) this.chooseTier(parseInteger(option.dataset.index));
      });

      this.tierList.addEventListener('mousemove', (event) => {
        const option = event.target.closest('[role="option"]');
        if (option && parseInteger(option.dataset.index) !== this.activeOption) this.setActiveOption(parseInteger(option.dataset.index));
      });

      this.tierList.addEventListener('keydown', (event) => {
        const last = this.tierOptions.length - 1;
        const moves = { ArrowDown: this.activeOption + 1, ArrowUp: this.activeOption - 1, Home: 0, End: last, PageDown: last, PageUp: 0 };
        if (Object.prototype.hasOwnProperty.call(moves, event.key)) {
          event.preventDefault();
          this.setActiveOption(Math.max(0, Math.min(last, moves[event.key])));
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.chooseTier(this.activeOption);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          this.closeTierList(true);
        } else if (event.key === 'Tab') {
          this.closeTierList(false);
        }
      });

      // clique ou toque fora fecha a lista sem trocar a caixa
      document.addEventListener('pointerdown', (event) => {
        if (this.isTierListOpen() && this.tierSelectWrap && !this.tierSelectWrap.contains(event.target)) this.closeTierList(false);
      });

      this.categoriesElement.addEventListener('click', (event) => {
        const button = event.target.closest('[data-combo-action]');
        if (!button) return;
        const row = button.closest('[data-flavor-key]');
        if (!row || row.dataset.available !== 'true') return;
        const isIncrease = button.dataset.comboAction === 'increase';
        // AdGrow (14/09/2026, P18): botão travado continua focável (aria-disabled); a ação é bloqueada aqui
        if (button.getAttribute('aria-disabled') === 'true') {
          if (isIncrease && this.getTotalCount() >= this.maxQuantity) this.showLimitNotice();
          return;
        }
        this.setCount(row.dataset.flavorKey, (this.counts[row.dataset.flavorKey] || 0) + (isIncrease ? 1 : -1));
      });

      this.categoriesElement.addEventListener('input', (event) => {
        const input = event.target.closest('[data-combo-input]');
        if (!input) return;
        const row = input.closest('[data-flavor-key]');
        if (!row || row.dataset.available !== 'true') return;
        this.clearNotice();
        this.setCount(row.dataset.flavorKey, input.value);
      });

      // AdGrow (14/09/2026, ajustes do Rodrigo): com a caixa incompleta o botão não trava; o clique mostra o que falta
      // o principal sempre adiciona e vai ao carrinho, com ou sem os secundários
      // AdGrow (24/09/2026, A2): com "Três ações no painel" ligado (stayAfterAdd) o principal adiciona e fica na página,
      // mostrando a confirmação; desligado, adiciona e vai ao carrinho, como sempre.
      this.buyButton.addEventListener('click', () => {
        // AdGrow (24/09/2026, frente C2): em modo edição o principal salva (troca as linhas da caixa) e vai ao carrinho
        if (this.isReady()) this.addToCart(this.editando ? 'editar' : this.config.stayAfterAdd ? 'ficar' : 'carrinho');
        else this.guideToFlavors();
      });

      // "Montar outra caixa" vive na confirmação: a caixa já foi adicionada, aqui só se começa a próxima
      if (this.buyAnother) {
        this.buyAnother.addEventListener('click', () => this.startAnother());
      }

      if (this.detailToggle) {
        this.detailToggle.addEventListener('click', () => this.toggleDetail(!this.detailOpen));
        // Esc com os detalhes abertos fecha e devolve o foco ao botão
        this.progressElement.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape' || !this.detailOpen) return;
          event.preventDefault();
          this.toggleDetail(false);
          this.detailToggle.focus();
        });
      }
      if (this.buyCheckout) {
        this.buyCheckout.addEventListener('click', () => {
          if (this.isReady()) this.addToCart('checkout');
          else this.guideToFlavors();
        });
      }

      this.bindPresets();
    }

    // Com sabores na tela, só balança o progresso. Sem sabores na tela (cliente lá embaixo, na calculadora), rola até a
    // etapa 2, põe o foco no título e balança depois que a rolagem termina.
    guideToFlavors() {
      const list = this.categoriesElement.getBoundingClientRect();
      const flavorsOnScreen = list.bottom > 80 && list.top < window.innerHeight - 160;
      if (flavorsOnScreen) {
        this.callAttention();
        return;
      }
      const target = this.flavorsElement || this.categoriesElement;
      const smooth = !prefersReducedMotion();
      target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
      if (this.flavorsHeading) this.flavorsHeading.focus({ preventScroll: true });
      clearTimeout(this.guideTimer);
      this.guideTimer = setTimeout(() => this.callAttention(), smooth ? 550 : 0);
    }

    callAttention() {
      if (!this.progressElement) return;
      this.progressElement.classList.remove('is-chamando');
      void this.progressElement.offsetWidth;
      this.progressElement.classList.add('is-chamando');
      clearTimeout(this.attentionTimer);
      this.attentionTimer = setTimeout(() => this.progressElement.classList.remove('is-chamando'), 1400);
    }

    // AdGrow (14/09/2026, ajustes do Rodrigo): (1) o painel de compra só aparece quando a rolagem chega nos sabores, isto é,
    // quando o topo da lista sobe até a metade da tela (ao abrir a página a lista ainda está na metade de baixo, então o painel
    // fica escondido até o cliente rolar); na primeira vez, com a caixa vazia, as marcas fazem uma onda. (2) O WhatsApp flutuante sai de cima do painel quando cai na faixa da coluna de compra (regra 2f do D14).
    observeViewport() {
      // AdGrow (24/09/2026, A2): a altura real da barra vai para --bewow-combo-bar-height (o bloco de sabores reserva esse
      // espaço embaixo, então o último sabor e os controles ficam acima da barra no fim da rolagem)
      this.measureBar();
      if (this.progressElement && 'ResizeObserver' in window) {
        new ResizeObserver(() => this.measureBar()).observe(this.progressElement);
      }
      if (this.progressElement) {
        let frame = 0;
        const reveal = () => {
          frame = 0;
          // AdGrow (24/09/2026, pedido do Rodrigo): o card aparece assim que a rolagem chega no primeiro sabor (Uva), no
          // computador e no celular. Conta a partir da linha do primeiro sabor visível: quando ela passa inteira para cima do
          // espaço que o card ocupa na base da tela, o card entra logo abaixo dela, sem cobri-la. Sem linha visível (categoria
          // recolhida), vale o topo da lista. Antes era o topo da lista chegando à metade da tela, o que atrasava o card.
          const primeira = Array.from(this.categoriesElement.querySelectorAll('.bewow-combo-flavor')).find(
            (linha) => linha.offsetParent !== null
          );
          // o ponto de entrada é o mesmo nos três estados (montando, caixa completa e adicionada): a reserva usa a altura do
          // card MONTANDO, guardada enquanto ele está nesse estado. Com a altura atual, o card completo (cerca de 450 px)
          // só apareceria bem mais tarde e sumiria antes.
          if (!this.progressElement.classList.contains('is-completa') || !this.alturaMontando) {
            if (!this.progressElement.classList.contains('is-completa')) this.alturaMontando = this.progressElement.offsetHeight;
          }
          const reserva = (this.alturaMontando || this.progressElement.offsetHeight) + 12;
          const reached = primeira
            ? primeira.getBoundingClientRect().bottom <= window.innerHeight - reserva
            : this.categoriesElement.getBoundingClientRect().top <= window.innerHeight * 0.5;
          if (this.progressElement.classList.contains('is-oculto') !== reached) return;
          this.progressElement.classList.toggle('is-oculto', !reached);
          if (reached && !this.waveDone) {
            this.waveDone = true;
            if (this.getTotalCount() === 0 && !prefersReducedMotion()) {
              this.progressElement.classList.add('is-onda');
              setTimeout(() => this.progressElement.classList.remove('is-onda'), 1000 + this.progressCapacity * 45);
            }
          }
        };
        const schedule = () => {
          if (!frame) frame = requestAnimationFrame(reveal);
        };
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);
        reveal();
      }

      if (!('IntersectionObserver' in window)) return;

      const whatsapp = document.querySelector('.whatsapp-float');
      if (whatsapp && this.flavorsElement) {
        const markOverlap = () => {
          const zap = whatsapp.getBoundingClientRect();
          const column = this.flavorsElement.getBoundingClientRect();
          document.body.classList.toggle('bewow-zap-sobre-coluna', zap.left < column.right + 8 && zap.right > column.left - 8);
        };
        markOverlap();
        window.addEventListener('resize', markOverlap);
        new IntersectionObserver((entries) => {
          entries.forEach((entry) => document.body.classList.toggle('bewow-montando', entry.isIntersecting));
        }).observe(this.flavorsElement);
      }
      // AdGrow (24/09/2026, A2): etapa 1 na tela. No celular o WhatsApp caía exatamente sobre o cartão pré-selecionado
      // (medido em 23/09); enquanto os cartões estão à vista ele sai (CSS em body.bewow-escolhendo-caixa).
      if (this.tierElement) {
        new IntersectionObserver((entries) => {
          entries.forEach((entry) => document.body.classList.toggle('bewow-escolhendo-caixa', entry.isIntersecting));
        }).observe(this.tierElement);
      }
    }

    // AdGrow (24/09/2026, card em três estados): o card entra com uma transição curta quando a caixa fica completa e quando a
    // caixa é adicionada. Sem animação para quem pediu menos movimento no sistema.
    playEnter() {
      if (!this.progressElement || prefersReducedMotion()) return;
      this.progressElement.classList.remove('is-entrando');
      void this.progressElement.offsetWidth;
      this.progressElement.classList.add('is-entrando');
      clearTimeout(this.enterTimer);
      this.enterTimer = setTimeout(() => this.progressElement.classList.remove('is-entrando'), 700);
    }

    measureBar() {
      if (!this.progressElement) return;
      this.root.style.setProperty('--bewow-combo-bar-height', `${Math.round(this.progressElement.offsetHeight)}px`);
    }

    snapshot() {
      return `${this.tierIndex}|${JSON.stringify(this.counts)}`;
    }

    // AdGrow (24/09/2026, A2): estado de confirmação da barra. Verdadeiro logo depois de adicionar (com stayAfterAdd):
    // a linha de compra dá lugar a "Ir para o carrinho" e "Montar outra caixa". Qualquer mudança na caixa desfaz.
    setConfirmed(value) {
      this.confirmed = Boolean(value);
      if (!this.progressElement) return;
      const entrando = this.confirmed && !this.progressElement.classList.contains('is-confirmada');
      this.progressElement.classList.toggle('is-confirmada', this.confirmed);
      // AdGrow (24/09/2026, card em três estados): na confirmação o resumo da caixa continua à vista (é o que a pessoa acabou
      // de mandar); só as ações trocam e a faixa entra no topo
      if (this.actionsBox) this.actionsBox.hidden = this.confirmed;
      if (this.doneBanner) this.doneBanner.hidden = !this.confirmed;
      if (this.listTitle) {
        this.listTitle.textContent = this.confirmed
          ? this.listTitle.dataset.adicionada || this.listTitle.dataset.montando
          : this.listTitle.dataset.montando;
      }
      if (entrando) this.playEnter();
      if (this.doneBox) this.doneBox.hidden = !this.confirmed;
      if (this.confirmed) this.toggleDetail(false);
      this.measureBar();
    }

    // "Ver caixa": abre e fecha os detalhes (sabores, preço por garrafa, economia, Comprar agora) sem mexer na seleção
    toggleDetail(open) {
      if (!this.detail || !this.detailToggle) return;
      this.detailOpen = Boolean(open);
      this.detail.hidden = !this.detailOpen;
      this.detailToggle.setAttribute('aria-expanded', String(this.detailOpen));
      this.detailToggle.textContent = this.detailOpen
        ? this.detailToggle.dataset.close || this.detailToggle.dataset.open
        : this.detailToggle.dataset.open;
      this.progressElement.classList.toggle('is-aberta', this.detailOpen);
      this.measureBar();
    }

    // lista dos sabores escolhidos com quantidades: o mesmo this.counts que vai ao carrinho, nada calculado à parte
    renderDetail() {
      if (!this.detailList) return;
      const seen = new Set();
      const items = this.categories
        .flatMap((category) => category.flavors)
        .filter((flavor) => (this.counts[flavor.key] || 0) > 0 && !seen.has(flavor.key) && seen.add(flavor.key))
        .map((flavor) => {
          const item = createElement('li', 'bewow-combo-builder__detail-item');
          item.append(
            createElement('b', 'bewow-combo-builder__detail-qty', `${this.counts[flavor.key]}×`),
            createElement('span', 'bewow-combo-builder__detail-name', flavor.name)
          );
          return item;
        });
      this.detailList.replaceChildren(...items);
      this.detailList.hidden = !items.length;
      if (this.flavorCount) {
        const k = items.length;
        this.flavorCount.textContent =
          k === 1
            ? this.texts.summaryFlavorsOne || '1 sabor escolhido'
            : fillTemplate(this.texts.summaryFlavorsMany || '{k} sabores escolhidos', { k });
      }
      if (this.detailEmpty) this.detailEmpty.hidden = items.length > 0;
    }

    // "Montar outra caixa" na confirmação: a caixa já está no carrinho; esvazia o construtor, avisa e leva aos sabores.
    // (O addToCart('outra') de 23/09 adicionava e limpava num passo só; agora quem adiciona é o principal.)
    startAnother() {
      if (this.adding) return;
      this.setConfirmed(false);
      this.clearFlavors();
      this.showNotice(this.texts.buyAnotherNext || 'Monte a próxima caixa.');
      const alvo = this.flavorsElement || this.categoriesElement;
      if (alvo) alvo.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
      if (this.flavorsHeading) this.flavorsHeading.focus({ preventScroll: true });
    }

    // AdGrow (14/09/2026, pedido do Rodrigo): sugestões de sabores, com a mesma regra do D14 (rodada 3 de 10/09/2026).
    // Cada sugestão completa só as garrafas que faltam, sem apagar o que o cliente escolheu. Com a caixa como a última
    // sugestão deixou, a troca refaz só a parte da sugestão; com a caixa cheia montada à mão, a sugestão substitui tudo e
    // aparece o link para voltar à seleção anterior.
    bindPresets() {
      if (!this.presetsRoot) return;
      this.presetButtons.forEach((button) => {
        // AdGrow (24/09/2026): sugestão que não consegue preencher nada (todos os sabores ocultos ou
        // esgotados, hoje "Lançamentos") some em vez de aparecer apagada. presetFlavors só devolve
        // sabor disponível, então a lista vazia é o sinal.
        if (!this.presetFlavors(button, 12).length) {
          button.hidden = true;
          button.dataset.semSabores = 'true';
        }
        button.addEventListener('click', () => this.applyPreset(button));
      });
      if (this.presetUndo) this.presetUndo.addEventListener('click', () => this.undoPreset());
      if (this.presetClear) this.presetClear.addEventListener('click', () => this.clearFlavors());
      if (this.presetToggle) {
        this.presetToggle.addEventListener('click', () => {
          this.presetExpanded = !this.presetExpanded;
          this.layoutPresets();
        });
      }
      if (window.matchMedia) {
        this.presetCarouselQuery = window.matchMedia('(max-width: 749px)');
        const trocar = () => this.layoutPresets();
        if (this.presetCarouselQuery.addEventListener) this.presetCarouselQuery.addEventListener('change', trocar);
        else if (this.presetCarouselQuery.addListener) this.presetCarouselQuery.addListener(trocar);
      }
      this.layoutPresets();
    }

    // AdGrow (24/09/2026): carrossel infinito das sugestões no celular. A lista fica [cópias][originais][cópias]; ao passar
    // da metade de um dos lados, a rolagem volta a mesma distância de um conjunto inteiro, sem salto visível. As cópias são
    // só visuais (aria-hidden, fora da tabulação): o toque numa cópia aciona a sugestão original, e o estado de "aplicada"
    // das originais é espelhado nelas.
    buildPresetCarousel(visiveis) {
      const lista = this.presetButtons[0] && this.presetButtons[0].parentElement;
      if (!lista || this.presetCarousel || visiveis.length < 2) return;
      const copiar = (lado) =>
        visiveis.map((original) => {
          const copia = original.cloneNode(true);
          copia.removeAttribute('data-combo-preset');
          copia.removeAttribute('id');
          copia.setAttribute('aria-hidden', 'true');
          copia.tabIndex = -1;
          copia.dataset.presetCopia = lado;
          copia.addEventListener('click', () => original.click());
          copia._original = original;
          return copia;
        });
      const antes = copiar('antes');
      const depois = copiar('depois');
      antes.forEach((copia) => lista.insertBefore(copia, visiveis[0]));
      const ancora = this.presetToggle && this.presetToggle.parentElement === lista ? this.presetToggle : null;
      depois.forEach((copia) => lista.insertBefore(copia, ancora));
      lista.classList.add('is-carrossel');
      const conjunto = () => depois[0].offsetLeft - visiveis[0].offsetLeft;
      const aoRolar = () => {
        const largura = conjunto();
        if (largura <= 0) return;
        if (lista.scrollLeft < largura * 0.5) lista.scrollLeft += largura;
        else if (lista.scrollLeft > largura * 1.5) lista.scrollLeft -= largura;
      };
      lista.addEventListener('scroll', aoRolar, { passive: true });
      const espelho = new MutationObserver(() => {
        [...antes, ...depois].forEach((copia) => {
          copia.classList.toggle('is-aplicada', copia._original.classList.contains('is-aplicada'));
        });
      });
      visiveis.forEach((original) => espelho.observe(original, { attributes: true, attributeFilter: ['class'] }));
      requestAnimationFrame(() => { lista.scrollLeft = visiveis[0].offsetLeft - lista.offsetLeft - 16; aoRolar(); });
      this.presetCarousel = { lista, copias: [...antes, ...depois], aoRolar, espelho };
    }

    destroyPresetCarousel() {
      if (!this.presetCarousel) return;
      const { lista, copias, aoRolar, espelho } = this.presetCarousel;
      copias.forEach((copia) => copia.remove());
      lista.removeEventListener('scroll', aoRolar);
      espelho.disconnect();
      lista.classList.remove('is-carrossel');
      lista.scrollLeft = 0;
      this.presetCarousel = null;
    }

    // AdGrow (24/09/2026): as primeiras N sugestões (campo "Sugestões visíveis antes de Ver todas") ficam à vista;
    // o botão revela ou recolhe as demais no lugar. A sugestão aplicada nunca é recolhida, para o cliente ver
    // qual está marcada. Sem sugestão extra, o botão não aparece.
    layoutPresets() {
      if (!this.presetsRoot) return;
      const visiveis = this.presetButtons.filter((button) => button.dataset.semSabores !== 'true');
      // AdGrow (24/09/2026, pedido do Rodrigo): no celular as sugestões viram um carrossel com rolagem infinita, com todas à
      // vista; "Ver todas" some. No computador continua o "Ver todas" com as primeiras N sugestões.
      if (this.presetCarouselQuery && this.presetCarouselQuery.matches) {
        visiveis.forEach((button) => { if (button.hidden) button.hidden = false; });
        if (this.presetToggle) this.presetToggle.hidden = true;
        this.buildPresetCarousel(visiveis);
        return;
      }
      this.destroyPresetCarousel();
      const limite = this.presetVisibleCount > 0 ? this.presetVisibleCount : visiveis.length;
      const extras = visiveis.slice(limite);
      visiveis.forEach((button, index) => {
        const recolhida = !this.presetExpanded && index >= limite && !button.classList.contains('is-aplicada');
        if (button.hidden !== recolhida) button.hidden = recolhida;
      });
      if (!this.presetToggle) return;
      const temExtras = extras.length > 0 && Boolean(this.presetToggle.dataset.more);
      this.presetToggle.hidden = !temExtras;
      const rotulo = this.presetExpanded ? this.presetToggle.dataset.less || this.presetToggle.dataset.more : this.presetToggle.dataset.more;
      if (this.presetToggle.textContent !== rotulo) this.presetToggle.textContent = rotulo;
      this.presetToggle.setAttribute('aria-expanded', String(this.presetExpanded));
    }

    // sabores disponíveis na ordem da página
    availableFlavors() {
      const seen = new Set();
      return this.categories
        .flatMap((category) => category.flavors)
        .filter((flavor) => {
          if (!flavor.available || seen.has(flavor.key)) return false;
          seen.add(flavor.key);
          return true;
        });
    }

    presetFlavors(button, quantity) {
      const flavors = this.availableFlavors();
      const rule = normalize(button.dataset.sabores || '');
      if (rule === 'todos') return flavors;
      if (rule === 'mais-vendidos' || rule === 'mais vendidos') {
        const firstCategory = (this.categories[0]?.flavors || []).filter((flavor) => flavor.available);
        return firstCategory.length ? firstCategory : flavors.slice(0, quantity <= 6 ? 3 : quantity <= 12 ? 4 : 6);
      }
      const handle = (value) =>
        normalize(value)
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      return String(button.dataset.sabores || '')
        .split('|')
        .map(handle)
        .filter(Boolean)
        .map((wanted) => flavors.find((flavor) => handle(flavor.name) === wanted || handle(flavor.variantName) === wanted))
        .filter(Boolean);
    }

    applyPreset(button) {
      const tier = this.tiers[this.tierIndex];
      if (this.adding || !tier) return;
      const chosen = this.presetFlavors(button, tier.quantity);
      if (!chosen.length) return;

      const current = JSON.stringify(this.counts);
      let base;
      let replaced = false;
      if (this.presetApplied && this.presetApplied.after === current) {
        base = JSON.parse(this.presetApplied.base);
      } else if (this.getTotalCount() >= tier.quantity) {
        this.presetUndoState = current;
        replaced = true;
        base = {};
      } else {
        base = JSON.parse(current);
      }

      Object.keys(this.counts).forEach((key) => {
        this.counts[key] = base[key] || 0;
      });
      const before = JSON.stringify(this.counts);
      let missing = tier.quantity - this.getTotalCount();
      for (let index = 0; missing > 0; index += 1, missing -= 1) {
        const key = chosen[index % chosen.length].key;
        this.counts[key] = (this.counts[key] || 0) + 1;
      }

      const name = button.textContent.trim();
      this.presetApplied = { name, base: before, after: JSON.stringify(this.counts) };
      this.clearNotice();
      this.updateUI();
      this.presetButtons.forEach((item) => {
        const on = item === button;
        item.classList.toggle('is-aplicada', on);
        item.setAttribute('aria-pressed', String(on));
      });
      // AdGrow (24/09/2026): a confirmação é o resultado real, escrito por updatePresets (caixa preenchida ou
      // quantas faltam). Só quando a sugestão substitui uma caixa montada à mão o aviso explica a troca,
      // porque é ele que acompanha o link "Voltar à minha seleção".
      if (this.presetHint && replaced) {
        this.presetHint.textContent = fillTemplate(this.texts.presetReplaced || 'Trocamos os sabores da caixa pela sugestão {nome}.', { nome: name });
      }
      if (this.presetUndo) this.presetUndo.hidden = !this.presetUndoState;
    }

    undoPreset() {
      if (this.adding || !this.presetUndoState) return;
      const viaKeyboard = Boolean(document.activeElement && document.activeElement.matches(':focus-visible'));
      const back = JSON.parse(this.presetUndoState);
      Object.keys(this.counts).forEach((key) => {
        this.counts[key] = back[key] || 0;
      });
      this.presetUndoState = null;
      this.presetApplied = null;
      this.clearNotice();
      this.updateUI();
      // o link some depois do clique: quem usa teclado segue na primeira sugestão, não no fim da página (com mouse ou toque, sem anel)
      if (viaKeyboard && this.presetButtons[0]) this.presetButtons[0].focus({ preventScroll: true });
    }

    clearFlavors() {
      if (this.adding) return;
      const viaKeyboard = Boolean(document.activeElement && document.activeElement.matches(':focus-visible'));
      Object.keys(this.counts).forEach((key) => {
        this.counts[key] = 0;
      });
      this.presetApplied = null;
      this.presetUndoState = null;
      this.clearNotice();
      this.updateUI();
      if (viaKeyboard && this.presetButtons[0]) this.presetButtons[0].focus({ preventScroll: true });
    }

    updatePresets(total, target) {
      if (!this.presetsRoot) return;
      // mexeu nos sabores à mão: a sugestão deixa de estar marcada e a seleção anterior deixa de valer
      if (this.presetApplied && this.presetApplied.after !== JSON.stringify(this.counts)) {
        this.presetApplied = null;
        this.presetUndoState = null;
      }
      if (!this.presetApplied) {
        this.presetButtons.forEach((button) => {
          button.classList.remove('is-aplicada');
          button.setAttribute('aria-pressed', 'false');
        });
      }
      if (this.presetUndo) this.presetUndo.hidden = !this.presetUndoState;
      if (this.presetClear) this.presetClear.hidden = total === 0;
      if (this.presetHint) {
        // AdGrow (24/09/2026): o texto parcial diz quantas faltam ({falta}, {faltam} = Falta/Faltam, {garrafas} = 1 garrafa/N garrafas)
        const falta = Math.max(0, target - total);
        const values = {
          n: target,
          x: total,
          falta,
          faltam: falta === 1 ? 'Falta' : 'Faltam',
          garrafas: falta === 1 ? '1 garrafa' : `${falta} garrafas`,
        };
        let text;
        if (total >= target) text = fillTemplate(this.texts.presetHintFull || 'Caixa preenchida. Você pode ajustar os sabores abaixo.', values);
        else if (total > 0) text = fillTemplate(this.texts.presetHintPartial || '{faltam} {garrafas} para completar a caixa.', values);
        else text = fillTemplate(this.texts.presetHint || 'Toque numa sugestão e completamos sua caixa.', values);
        if (this.presetHint.textContent !== text) this.presetHint.textContent = text;
      }
      this.layoutPresets();
    }

    getTotalCount() {
      return Object.values(this.counts).reduce((sum, count) => sum + count, 0);
    }

    isReady() {
      const tier = this.tiers[this.tierIndex];
      const total = this.getTotalCount();
      return Boolean(tier) && total > 0 && total === tier.quantity;
    }

    setCount(key, requestedValue) {
      const currentTotal = this.getTotalCount();
      const currentValue = this.counts[key] || 0;
      const totalWithoutCurrent = currentTotal - currentValue;
      const requested = Math.max(0, parseInteger(requestedValue));
      const value = Math.min(requested, Math.max(0, this.maxQuantity - totalWithoutCurrent));
      const previousTierIndex = this.tierIndex;
      this.counts[key] = value;

      const newTotal = this.getTotalCount();
      // AdGrow (14/09/2026, P02): a caixa escolhida pelo cliente manda. Subir sozinho só enquanto ele não escolheu a caixa
      // no seletor, e nunca descer sozinho (antes, "12 / 6" virava "11 / 12" no primeiro clique em "−").
      if (!this.manualTier && newTotal > this.tiers[this.tierIndex].quantity) {
        const fittingTierIndex = this.tiers.findIndex((tier) => newTotal <= tier.quantity);
        this.tierIndex = fittingTierIndex >= 0 ? fittingTierIndex : this.tiers.length - 1;
      }

      this.updateUI();

      // AdGrow (14/09/2026, P05 e C14): avisos no data-combo-message (role="status"), dentro do painel de compra.
      // "Caixa completa" não vira aviso: o próprio progresso já diz.
      const tier = this.tiers[this.tierIndex];
      if (this.tierIndex !== previousTierIndex) {
        this.flashPrice();
        this.showNotice(
          fillTemplate(this.texts.tierUp || 'Sua caixa passou para {n} garrafas, {preço} cada', {
            n: tier.quantity,
            'preço': formatCurrency(tier.unitPrice),
          })
        );
      } else if (requested > value || (value > currentValue && newTotal >= this.maxQuantity)) {
        this.showLimitNotice();
      }
    }

    updateUI() {
      const tier = this.tiers[this.tierIndex] || this.tiers[0];
      if (!tier) return;
      // AdGrow (24/09/2026, A2): mexeu na caixa depois de adicionar? A confirmação deixa de valer e a barra volta ao normal.
      if (this.confirmed && this.snapshot() !== this.confirmedSnapshot) this.setConfirmed(false);
      // AdGrow port V15 (14/09/2026): expõe a quantidade da caixa escolhida para a calculadora de frete
      if (this.root.dataset.tierQuantity !== String(tier.quantity) || this.root.dataset.tierUnitPrice !== String(tier.unitPrice)) {
        this.root.dataset.tierQuantity = String(tier.quantity);
        this.root.dataset.tierUnitPrice = String(tier.unitPrice);
        this.root.dispatchEvent(new CustomEvent('bewow:combo-tier', { bubbles: true, detail: { quantity: tier.quantity } }));
      }

      this.tierOptions.forEach((option, index) => {
        const selected = String(index === this.tierIndex);
        if (option.getAttribute('aria-selected') !== selected) option.setAttribute('aria-selected', selected);
        // o rótulo acompanha a seleção: "Escolhida" no marcado, convite nos demais
        const rotulo = option.querySelector('.bewow-combo-builder__option-chosen');
        if (rotulo) {
          const escolhido = index === this.tierIndex;
          const texto = escolhido ? this.texts.tierChosen : this.texts.tierChoose || this.texts.tierChosen;
          if (rotulo.textContent !== texto) rotulo.textContent = texto;
          rotulo.classList.toggle('is-oculto', !escolhido && !this.texts.tierChoose);
        }
      });

      const currentTotal = this.getTotalCount();
      const target = tier.quantity;
      // AdGrow (14/09/2026, ajustes do Rodrigo): o valor é o da caixa escolhida, não o das garrafas já escolhidas. A página
      // nunca mostra R$ 0,00 e o cliente vê o preço antes de montar a caixa. Riscado e economia sobre o preço de referência.
      const boxPrice = target * tier.unitPrice;
      const comparePrice = target * this.getReferenceUnitPrice();
      const hasCompare = comparePrice > boxPrice;
      const ready = currentTotal === target && currentTotal > 0;
      const atMaximum = currentTotal >= this.maxQuantity;
      const values = {
        ...this.tierValues(tier),
        x: currentTotal,
        valor: formatCurrency(Math.max(0, comparePrice - boxPrice)),
      };

      if (this.tierTitle) this.tierTitle.textContent = cleanSeparators(fillTemplate(this.texts.tierFaceTitle || 'Caixa com {n} garrafas', values));
      if (this.tierDetail) {
        const detail = this.texts.tierFaceDetail ? cleanSeparators(fillTemplate(this.texts.tierFaceDetail, values)) : '';
        fillDetail(this.tierDetail, detail);
        this.tierDetail.hidden = !detail;
      }
      if (this.tierOff) {
        this.tierOff.textContent = tier.discount || '';
        this.tierOff.hidden = !tier.discount;
      }

      this.totalElement.textContent = formatCurrency(boxPrice);
      if (this.unitPriceElement) this.unitPriceElement.textContent = fillTemplate(this.texts.unitPrice || '{preço} a garrafa', values);
      if (this.compareElement) this.compareElement.textContent = hasCompare ? formatCurrency(comparePrice) : '';
      if (this.compareLine) this.compareLine.hidden = !hasCompare;
      if (this.savingsElement) {
        const showSavings = hasCompare && Boolean(this.texts.savings);
        this.savingsElement.hidden = !showSavings;
        this.savingsElement.textContent = showSavings ? fillTemplate(this.texts.savings, values) : '';
      }

      // botão: aria-disabled com a caixa incompleta (o clique mostra o que falta); disabled só durante a adição
      setAriaDisabled(this.buyButton, !ready);
      // "Comprar agora" (dentro de "Ver caixa") só existe com a caixa completa: sem caixa não há o que adicionar
      // em modo edição (C2) some também: numa tela de correção, uma decisão só
      if (this.buyCheckout) this.buyCheckout.hidden = !ready || Boolean(this.editando);
      if (!this.adding) {
        this.buyButton.disabled = false;
        if (ready) {
          // o rótulo do principal também existe no Liquid (primeira pintura); em modo edição (C2) vale o campo de edição
          this.buyButton.textContent =
            (this.editando && this.config.editText) || this.config.readyText || 'Adicionar ao carrinho';
        } else if (currentTotal < target) {
          const falta = target - currentTotal;
          const template =
            falta === 1
              ? this.texts.buttonMissingOne || 'Escolha mais 1 garrafa'
              : this.texts.buttonMissingMany || 'Escolha mais {falta} garrafas';
          this.buyButton.textContent = fillTemplate(template, { ...values, falta });
        } else {
          this.buyButton.textContent = fillTemplate(this.texts.buttonExcess || 'Tire {excesso} da caixa', {
            ...values,
            excesso: currentTotal - target,
          });
        }
      }

      this.updateProgress(currentTotal, target, values);
      this.updatePresets(currentTotal, target);
      this.renderDetail();

      this.root.querySelectorAll('[data-flavor-key]').forEach((row) => {
        const key = row.dataset.flavorKey;
        const count = this.counts[key] || 0;
        const input = row.querySelector('[data-combo-input]');
        const decrease = row.querySelector('[data-combo-action="decrease"]');
        const increase = row.querySelector('[data-combo-action="increase"]');
        const unavailable = row.dataset.available !== 'true';

        if (input && input.value !== String(count)) input.value = String(count);
        // AdGrow (24/09/2026): sabor com quantidade ganha fundo suave e quantidade mais evidente (CSS)
        row.classList.toggle('is-escolhido', count > 0);
        if (decrease) setAriaDisabled(decrease, unavailable || count <= 0);
        if (increase) setAriaDisabled(increase, unavailable || atMaximum);
      });

      this.categoryCounters.forEach(({ element, keys }) => {
        const chosen = keys.reduce((sum, key) => sum + (this.counts[key] || 0), 0);
        const text = chosen > 0 ? ` (${chosen})` : '';
        if (element.textContent !== text) element.textContent = text;
      });
    }

    // AdGrow (14/09/2026): preço da garrafa avulsa (campo da seção); sem o campo, a faixa mais cara
    getReferenceUnitPrice() {
      return parseMoney(this.config.referenceUnitPrice) || Math.max(...this.tiers.map((item) => item.unitPrice));
    }

    // Progresso por garrafa, portado do tema no ar (D14, rodada 2 de 10/09/2026): uma marca por garrafa da caixa, a marca
    // nova pula ao encher, verde com a caixa completa e vermelho com garrafa a mais.
    updateProgress(total, target, values) {
      if (!this.progressElement || !this.progressSlots || !this.progressCount || !this.progressLeft) return;

      if (this.progressCapacity !== target) {
        this.progressSlots.replaceChildren(
          ...Array.from({ length: target }, (_, index) => {
            const slot = createElement('span', 'bewow-combo-builder__slot');
            slot.style.setProperty('--i', String(index));
            return slot;
          })
        );
        this.progressSlots.style.setProperty('--slots', String(target));
        this.progressCapacity = target;
      }

      const animate = !prefersReducedMotion();
      let newlyFilled = 0;
      this.progressSlots.querySelectorAll('.bewow-combo-builder__slot').forEach((slot, index) => {
        const full = index < total;
        if (full && !slot.classList.contains('is-cheia') && animate) {
          slot.style.animationDelay = `${newlyFilled * 30}ms`;
          newlyFilled += 1;
          slot.classList.remove('is-nova');
          void slot.offsetWidth;
          slot.classList.add('is-nova');
        }
        if (!full) {
          slot.classList.remove('is-nova');
          slot.style.animationDelay = '';
        }
        slot.classList.toggle('is-cheia', full);
      });

      const falta = target - total;
      if (this.badgeNumber) this.badgeNumber.textContent = String(target);
      // desconto da caixa escolhida ao lado do título do resumo (o mesmo texto do cartão da etapa 01, vindo das faixas de preço)
      if (!this.summaryOff) this.summaryOff = this.root.querySelector('[data-combo-summary-off]');
      if (this.summaryOff) {
        const desconto = (this.tiers[this.tierIndex] && this.tiers[this.tierIndex].discount) || '';
        if (this.summaryOff.textContent !== desconto) this.summaryOff.textContent = desconto;
        this.summaryOff.hidden = !desconto;
      }
      const eraCompleta = this.progressElement.classList.contains('is-completa');
      this.progressElement.classList.toggle('is-completa', falta === 0);
      if (falta === 0 && !eraCompleta && total > 0) this.playEnter();
      this.progressElement.classList.toggle('is-excesso', falta < 0);

      let countText;
      let leftText;
      if (falta === 0) {
        // AdGrow (24/09/2026, card em três estados): com a caixa completa o progresso diz só "12 de 12 garrafas"
        countText = fillTemplate(this.texts.progressDone || '{x} de {n} garrafas', values);
        leftText = '';
      } else if (falta < 0) {
        countText = fillTemplate(this.texts.progressExcess || '{x} garrafas para a caixa de {n}', values);
        leftText = fillTemplate(this.texts.progressExcessLeft || 'Tire {excesso}', { ...values, excesso: -falta });
      } else {
        const countTemplate =
          total === 0 ? this.texts.progressEmpty || 'Escolha {n} garrafas' : this.texts.progressPartial || '{x} de {n} garrafas';
        const leftTemplate = falta === 1 ? this.texts.progressLeftOne || 'Falta 1' : this.texts.progressLeftMany || 'Faltam {falta}';
        countText = fillTemplate(countTemplate, values);
        leftText = fillTemplate(leftTemplate, { ...values, falta });
      }
      if (this.progressCount.textContent !== countText) this.progressCount.textContent = countText;
      if (this.progressLeft.textContent !== leftText) this.progressLeft.textContent = leftText;
    }

    // Erros: ficam até a próxima ação que os troque. O elemento fica sempre no DOM (vazio, recolhido pelo CSS),
    // para o leitor de tela anunciar a mudança do role="status".
    showMessage(message) {
      if (!this.messageElement) return;
      this.noticeActive = false;
      this.messageElement.classList.remove('is-notice');
      this.messageElement.textContent = message;
    }

    // Avisos do montador: somem no próximo clique ou digitação
    showNotice(message) {
      if (!this.messageElement || !message) return;
      this.messageElement.classList.add('is-notice');
      this.messageElement.textContent = message;
      this.noticeActive = true;
    }

    showLimitNotice() {
      this.showNotice(fillTemplate(this.texts.limit || 'Limite de {n} garrafas por caixa', { n: this.maxQuantity }));
    }

    clearNotice() {
      if (!this.noticeActive || !this.messageElement) return;
      this.noticeActive = false;
      this.messageElement.classList.remove('is-notice');
      this.messageElement.textContent = '';
    }

    // AdGrow (23/09/2026): ao ficar na página, o contador do header precisa acompanhar
    async updateCartCount() {
      try {
        const r = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=cart-icon-bubble`);
        if (!r.ok) return;
        const dados = await r.json();
        const html = dados['cart-icon-bubble'];
        if (!html) return;
        const alvo = document.getElementById('cart-icon-bubble');
        // AdGrow (24/09/2026, A2): a seção renderizada traz só o MIOLO do link do carrinho (ícone, texto e bolha), dentro de
        // .shopify-section; não existe #cart-icon-bubble nela. A versão de 23/09 procurava esse id, não achava e mantinha o
        // HTML antigo: o contador nunca mudava. Mesmo caminho do Dawn (getSectionInnerHTML com '.shopify-section').
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const miolo = doc.querySelector('#cart-icon-bubble')?.innerHTML || doc.querySelector('.shopify-section')?.innerHTML;
        if (alvo && miolo) alvo.innerHTML = miolo;
      } catch (e) { /* contador desatualizado não impede a compra */ }
    }

    async addToCart(destino = 'carrinho') {
      const tier = this.tiers[this.tierIndex];
      if (this.adding || !tier || this.getTotalCount() !== tier.quantity) return;

      const items = [];
      // AdGrow (24/09/2026, pedido do carrinho C1): um id por caixa, igual em todas as linhas da mesma adição. O carrinho
      // agrupa as linhas por ele e só mostra "Editar sabores" quando existe.
      // AdGrow (24/09/2026, frente C2): editando, a caixa mantém o mesmo id, para o carrinho seguir tratando-a como a mesma
      const editar = destino === 'editar' && this.editando;
      const montagem = editar ? this.editando.id : `m-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      this.root.querySelectorAll('[data-flavor-key]').forEach((row) => {
        const key = row.dataset.flavorKey;
        const count = this.counts[key] || 0;
        const variantId = parseInteger(row.dataset.variantId);
        if (count > 0 && variantId && !items.some((item) => item.id === variantId)) {
          items.push({
            id: variantId,
            quantity: count,
            properties: {
              _Tipo: 'Item de Pacote',
              _Combo: this.config.productTitle || 'Monte seu combo',
              _Montagem: montagem,
            },
          });
        }
      });

      if (!items.length) {
        this.showMessage('Selecione ao menos um sabor disponível.');
        return;
      }

      this.adding = true;
      // o texto de "Adicionando..." vai no botão que a pessoa tocou, não sempre no principal
      const botaoAtivo =
        destino === 'outra' && this.buyAnother
          ? this.buyAnother
          : destino === 'checkout' && this.buyCheckout
            ? this.buyCheckout
            : this.buyButton;
      const rotuloAntes = botaoAtivo.textContent;
      botaoAtivo.disabled = true;
      botaoAtivo.textContent = (editar && this.config.savingText) || this.config.addingText || 'Adicionando...';
      this.showMessage('');

      // AdGrow (24/09/2026, frente C2): salvar a edição. Relê o carrinho agora (outra aba pode ter mexido), zera as linhas
      // desta caixa e só então adiciona as novas. Se zerar falhar, nada é adicionado (a caixa não duplica). Se a adição
      // falhar depois de zerar, devolve as linhas antigas como estavam.
      let antigas = [];
      if (editar) {
        try {
          antigas = this.linesOfBox(await this.cartRequest('cart.js'), montagem);
          if (antigas.length) {
            const updates = {};
            antigas.forEach((linha) => { updates[linha.key] = 0; });
            await this.cartRequest('cart/update.js', { updates });
          }
        } catch (error) {
          this.adding = false;
          botaoAtivo.disabled = false;
          botaoAtivo.textContent = rotuloAntes;
          this.showMessage(this.texts.editError || 'Não foi possível salvar a caixa. Ela continua no carrinho como estava.');
          this.updateUI();
          return;
        }
      }

      try {
        // AdGrow (17/09/2026): sem AbortController a promessa podia nunca resolver, e o botao
        // ficava em "Adicionando..." desabilitado ate a pessoa recarregar a pagina.
        const adgControle = new AbortController();
        const adgPrazo = setTimeout(() => adgControle.abort(), 15000);
        let response;
        try {
          response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
            signal: adgControle.signal,
          });
        } finally {
          clearTimeout(adgPrazo);
        }

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || 'Não foi possível adicionar o combo.');
        }

        if (editar) {
          window.location.href = this.config.cartUrl || '/cart';
          return;
        }

        if (destino === 'ficar') {
          // AdGrow (24/09/2026, A2): adiciona e fica na página. A seleção continua visível (a pessoa vê o que acabou de
          // adicionar) e a barra vira a confirmação, com o carrinho ou outra caixa. Mexer nos sabores desfaz a confirmação.
          this.adding = false;
          botaoAtivo.disabled = false;
          botaoAtivo.textContent = rotuloAntes;
          this.confirmedSnapshot = this.snapshot();
          this.setConfirmed(true);
          this.updateCartCount();
          if (this.goCart) this.goCart.focus({ preventScroll: true });
          return;
        }
        if (destino === 'outra') {
          // fica na página: esvazia a caixa e avisa, para a pessoa montar a próxima
          this.adding = false;
          botaoAtivo.disabled = false;
          botaoAtivo.textContent = rotuloAntes;
          this.clearFlavors();
          this.showMessage(this.texts.buyAnotherDone || 'Caixa adicionada ao carrinho. Monte a próxima.');
          this.updateCartCount();
          const alvo = this.flavorsElement || this.categoriesElement;
          if (alvo) alvo.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
          return;
        }
        window.location.href = destino === 'checkout' ? '/checkout' : this.config.cartUrl || '/cart';
      } catch (error) {
        if (editar && antigas.length) {
          // a caixa antiga já saiu do carrinho: devolve as mesmas linhas, com as mesmas propriedades
          try {
            await this.cartRequest('cart/add.js', {
              items: antigas.map((linha) => ({ id: linha.variant_id, quantity: linha.quantity, properties: linha.properties })),
            });
          } catch (e) {
            console.error('[Bewow] caixa em edição removida e não devolvida ao carrinho', e);
          }
          this.adding = false;
          botaoAtivo.disabled = false;
          botaoAtivo.textContent = rotuloAntes;
          this.showMessage(this.texts.editError || 'Não foi possível salvar a caixa. Ela continua no carrinho como estava.');
          this.updateUI();
          return;
        }
        this.adding = false;
        botaoAtivo.disabled = false;
        botaoAtivo.textContent = rotuloAntes;
        // AdGrow (17/09/2026): tempo esgotado nao vira aviso. O que resolve o travamento e o
        // botao voltar a funcionar; texto de erro aqui so assusta quem ja vai tocar de novo.
        this.showMessage(
          error && error.name === 'AbortError'
            ? ''
            : error.message || 'Ocorreu um erro ao adicionar o combo.'
        );
        this.updateUI();
      }
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.bewowComboInitialized === 'true') return;
      root.dataset.bewowComboInitialized = 'true';
      root._adgCombo = new BewowComboBuilder(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));

  // AdGrow (17/09/2026): o caminho de sucesso navega para o carrinho sem zerar `adding`. Safari do
  // iPhone restaura a pagina do cache de historico com o estado intacto, entao voltar trazia o
  // botao desabilitado em "Adicionando..." e todo clique morria no guard do addToCart.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const construtor = root._adgCombo;
      if (!construtor) return;
      construtor.adding = false;
      construtor.updateUI();
    });
  });
})();
